import React from 'react';
import { Accident } from '../types';
import { MapPin, Navigation, AlertTriangle, ThumbsUp, ArrowRight } from 'react-feather';
import './AccidentCard.css';

interface AccidentCardProps {
  accident: Accident;
  onViewOnMap: (accident: Accident) => void;
  onUserClick: (username: string) => void;
  onReplacementRequest: (accident: Accident) => void;
  isUserBlocked: boolean;
  isTopItem?: boolean;
}

export const AccidentCard: React.FC<AccidentCardProps> = ({
  accident,
  onViewOnMap,
  onUserClick,
  onReplacementRequest,
  isUserBlocked,
  isTopItem = false
}) => {
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };
  const formatTimeAgo = (timestamp: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - timestamp.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const formatActualTime = (timestamp: Date) => {
    return timestamp.toLocaleTimeString('en-AU', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const formatDistance = (distance?: number) => {
    if (distance === undefined || distance === null) return '';
    if (distance === 0) return 'Same location';
    if (distance < 0.1) return `${Math.round(distance * 1000)}m away`;
    if (distance < 1) return `${Math.round(distance * 1000)}m away`;
    return `${distance.toFixed(1)}km away`;
  };

  const handleDirections = () => {
    const { lat, lng } = accident.location;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(url, '_blank');
  };

  return (
    <div className={`accident-card ${isTopItem ? 'top-item' : ''}`}>
      <div className="accident-header">
        <div className="accident-icon waze-icon">
          <AlertTriangle size={14} />
        </div>
        <div className="accident-content">
          <div className="title-row">
            <h3 className="accident-title">{accident.title}</h3>
            {isTopItem && <span className="priority-badge">New</span>}
          </div>
          <div className="accident-subtitle">
            <span className="user-label">by:</span>
            <button 
              className={`user-button ${isUserBlocked ? 'blocked' : ''}`} 
              onClick={() => onUserClick(accident.submittedBy)}
            >
              {accident.submittedBy}
            </button>
          </div>
        </div>
      </div>

      <div className="accident-meta">
        <span className="accident-time-pill">{formatTimeAgo(accident.timestamp)}</span>
        <span className="accident-time-pill">{formatActualTime(accident.timestamp)}</span>
        {(accident as any).distance !== undefined && (
          <span className="accident-distance">{formatDistance((accident as any).distance)}</span>
        )}
        <span className="accident-rating-pill">
          <ThumbsUp size={10} />
          {accident.helpfulnessRating || 3}
        </span>
      </div>

      <p className="accident-description">{accident.description}</p>

      <div className="accident-details">
        <div className="accident-detail">
          <MapPin size={14} className="detail-icon" />
          <span>{accident.location.address}</span>
        </div>
        <div className="accident-detail">
          <Navigation size={14} className="detail-icon" />
          <span>{accident.affectedLanes} lane{accident.affectedLanes !== 1 ? 's' : ''} affected</span>
        </div>
        <div className="accident-detail">
          <div 
            className="severity-indicator"
            style={{ backgroundColor: getSeverityColor(accident.severity) }}
          />
          <span>
            {accident.severity.charAt(0).toUpperCase() + accident.severity.slice(1)} severity
          </span>
        </div>
      </div>

      <div className="replacement-request-section">
        <button 
          className="btn-replacement-request" 
          onClick={() => onReplacementRequest(accident)}
        >
          <ArrowRight size={14} />
          Request replacement car
        </button>
      </div>

      <div className="accident-actions">
        <button className="btn-secondary" onClick={() => onViewOnMap(accident)}>
          <MapPin size={14} />
          View on Map
        </button>
        <button className="btn-primary" onClick={handleDirections}>
          <Navigation size={14} />
          Directions
        </button>
      </div>
    </div>
  );
}; 